import type { IContractBook, SupportedChainId } from "@eulerxyz/core-sdk"
import { contractBook } from "@eulerxyz/core-sdk"
import type { ContractFunctionConfig } from "@eulerxyz/evc-sdk"
import type { Address } from "viem"
import { BaseTransactionFactory } from "./BaseTransactionFactory"
import {
  MerklTransactionItem,
  type RewardStreamsTransactionItem,
  UnlockREulTransactionItem,
  isClaimRewardTransaction,
  isDisableRewardTransaction,
  isEnableRewardTransaction,
  isMerklTransaction,
  isUnlockREulTransaction,
} from "./types"

export class RewardStreamsTransactionFactory extends BaseTransactionFactory {
  rewardSteamsContractAddress: Address
  rewardSteamsAbi: typeof this.contractBook.rewardStreams.abi

  merklContractAddress: Address
  merklAbi: typeof this.contractBook.merklDistributor.abi

  rEulContractAddress: Address
  rEulAbi: typeof this.contractBook.rEul.abi

  constructor(chainId: SupportedChainId, contractBook: IContractBook) {
    super(chainId, contractBook)
    const address = contractBook.rewardStreams.address[chainId]
    const merklAddress = contractBook.merklDistributor.address[chainId]
    const rEulAddress = contractBook.rEul.address[chainId]
    if (!address || !merklAddress || !rEulAddress) {
      console.error(
        "[RewardStreamsTransactionFactory] address",
        address,
        " merklAddress ",
        merklAddress,
        " rEulAddress ",
        rEulAddress,
      )
      throw new Error("RewardStreams contract not found")
    }
    this.rewardSteamsContractAddress = address
    this.rewardSteamsAbi = this.contractBook.rewardStreams.abi

    this.merklContractAddress = merklAddress
    this.merklAbi = this.contractBook.merklDistributor.abi

    this.rEulContractAddress = rEulAddress
    this.rEulAbi = this.contractBook.rEul.abi
  }

  typeGuardConvertMultiple(
    _transactions: RewardStreamsTransactionItem[], // todo: modify this to use more specific type
  ): ContractFunctionConfig[] {
    const contractCallReadyTxs: ContractFunctionConfig[] = []
    for (const tx of _transactions) {
      const call = this.typeGuardConvertSingle(tx)
      contractCallReadyTxs.push(call)
    }
    return contractCallReadyTxs
  }

  typeGuardConvertSingle(
    _transaction:
      | RewardStreamsTransactionItem
      | UnlockREulTransactionItem
      | MerklTransactionItem, // todo: modify this to use more specific type
  ): ContractFunctionConfig {
    if (isEnableRewardTransaction(_transaction)) {
      return this.convertEnableReward(_transaction)
    }

    if (isDisableRewardTransaction(_transaction)) {
      return this.convertDisableReward(_transaction)
    }

    if (isClaimRewardTransaction(_transaction)) {
      return this.convertClaimReward(_transaction)
    }

    if (isMerklTransaction(_transaction)) {
      return this.convertClaimMerklReward(_transaction)
    }

    if (isUnlockREulTransaction(_transaction)) {
      return this.convertUnlockREulTransaction(_transaction)
    }

    throw this.errorUnsupportedTransactionType(_transaction)
  }

  convertEnableReward(
    _transaction: RewardStreamsTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.rewardStreams.abi> {
    return {
      functionName: "enableReward",
      abi: this.rewardSteamsAbi,
      address: this.rewardSteamsContractAddress,
      args: [_transaction.rewarded, _transaction.reward],
      onBehalfOfAccount: _transaction.from,
      value: BigInt(0),
    }
  }

  convertDisableReward(
    _transaction: RewardStreamsTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.rewardStreams.abi> {
    return {
      functionName: "disableReward",
      abi: this.rewardSteamsAbi,
      address: this.rewardSteamsContractAddress,
      args: [
        _transaction.rewarded,
        _transaction.reward,
        Boolean(_transaction.ignoreRecentReward),
      ],
      onBehalfOfAccount: _transaction.from,
      value: BigInt(0),
    }
  }

  convertClaimReward(
    _transaction: RewardStreamsTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.rewardStreams.abi> {
    return {
      functionName: "claimReward",
      abi: this.rewardSteamsAbi,
      address: this.rewardSteamsContractAddress,
      args: [
        _transaction.rewarded,
        _transaction.reward,
        _transaction.to,
        Boolean(_transaction.ignoreRecentReward),
      ],
      onBehalfOfAccount: _transaction.from,
      value: BigInt(0),
    }
  }

  private convertClaimMerklReward(
    _transaction: MerklTransactionItem,
  ): ContractFunctionConfig<typeof this.merklAbi> {
    return {
      functionName: "claim",
      abi: contractBook.merklDistributor.abi,
      address: this.merklContractAddress,
      args: [
        [_transaction.address],
        [_transaction.reward],
        [_transaction.accumulated],
        [_transaction.proof],
      ],
      onBehalfOfAccount: _transaction.from,
      value: BigInt(0),
    }
  }

  private convertUnlockREulTransaction(
    _transaction: UnlockREulTransactionItem,
  ): ContractFunctionConfig<typeof this.rEulAbi> {
    return {
      functionName: "withdrawToByLockTimestamp",
      abi: contractBook.rEul.abi,
      address: this.rEulContractAddress,
      args: [_transaction.account, BigInt(_transaction.lockTimestamp), true],
      onBehalfOfAccount: _transaction.account,
      value: BigInt(0),
    }
  }
}
